import moment from "moment";
import { Icon, Loader, Menu, Message, Popup } from "semantic-ui-react";
import styled from "styled-components";

import type { OpenAPI } from "simplydo/interfaces";

import { EmptyBox } from "../UI";

const StyledDownloadContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 5px;
  ${(props) =>
    !props.$disabled &&
    `
    &:hover {
      background-color: rgb(233, 235, 238);
      border-radius: 5px;
    }
  `}
`;

const DownloadsContainer = styled.div`
  max-height: 500px;
  overflow: auto;
  padding-right: 5;
`;

type DownloadsProps = {
  downloadCompleted: boolean;
  downloadsQuery: {
    data: OpenAPI.GET<"/users/{user_id}/downloads">["response"];
  };
};

const FileIconMap = {
  pdf: "file pdf outline",
  doc: "file word outline",
  docx: "file word outline",
  xls: "file excel outline",
  xlsx: "file excel outline",
  ppt: "file powerpoint outline",
  pptx: "file powerpoint outline",
  jpg: "file image outline",
  jpeg: "file image outline",
  png: "file image outline",
  gif: "file image outline",
  mp4: "file video outline",
  mp3: "file audio outline",
  zip: "file archive outline",
  rar: "file archive outline",
  txt: "file text outline",
  other: "file outline",
  csv: "file alternate outline",
};

const downloadTypeMap = {
  ideaPdfExport: "Idea PDF export",
  challengePdfExport: "Challenge engagement report",
  challengeIdeaPdfExport: "Challenge idea PDF export",
  assessmentXlsxExport: "Challenge assessment report",
  userAssessmentXlsxExport: "User assessment report",
  ideadDocxExport: "Idea docx export",
  pollPdfExport: "Poll results report",
};

const Downloads = ({ downloadCompleted, downloadsQuery }: DownloadsProps) => {
  if (
    !(
      downloadsQuery?.data?.pendingDownloads?.length > 0 ||
      downloadsQuery?.data?.downloads?.length > 0
    )
  ) {
    return <EmptyBox message="You have not created any downloads" />;
  }
  return (
    <div>
      <DownloadsContainer>
        {downloadsQuery?.data?.pendingDownloads?.length > 0 && (
          <>
            {!downloadCompleted && (
              <Message
                info
                content="Your download has started, we will notify you once it is completed."
              />
            )}
            <h5>Pending downloads</h5>
            {downloadsQuery?.data?.pendingDownloads.map((download) => (
              <Menu.Item
                key={download._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  textOverflow: "ellipsis",
                }}
                fitted
                link
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "98%",
                  }}
                >
                  <p>
                    <Icon name="file pdf outline" style={{ marginRight: 5 }} />
                    {download.context.displayName}
                  </p>
                  <Loader active inline size="tiny" />
                </div>
              </Menu.Item>
            ))}
          </>
        )}
        {downloadsQuery?.data?.downloads?.length > 0 && (
          <>
            {downloadCompleted && (
              <Message
                info
                succes
                content="Your download has finished, you can download it by clicking below."
              />
            )}
            <h5>Completed downloads</h5>
            {downloadsQuery?.data?.downloads.map((download) => (
              <Popup
                key={download._id}
                disabled={download.status === "success"}
                trigger={
                  <Menu.Item
                    as={download.url ? "a" : "div"}
                    download={download.context.displayName}
                    href={download.url ? download.url : ""}
                    style={{
                      display: "flex",
                      textOverflow: "ellipsis",
                      marginBottom: 5,
                      color: "black",
                      cursor:
                        download.status !== "failed" &&
                        download.status !== "expired" &&
                        "pointer",
                      paddingBottom: 5,
                      borderBottom: "1px solid rgba(34,36,38,.15)",
                      opacity:
                        (
                          download.status === "failed" ||
                          download.status === "expired"
                        ) ?
                          0.6
                        : 1,
                    }}
                  >
                    <StyledDownloadContainer
                      $disabled={download.status !== "success"}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Icon
                          name={
                            download?.context?.displayName?.includes(".") ?
                              FileIconMap[
                                download?.context?.displayName?.split(".").pop()
                              ]
                            : null
                          }
                          size="big"
                          style={{ marginRight: 12 }}
                        />
                        <div style={{ maxWidth: 420 }}>
                          <p
                            style={{
                              marginBottom: 4,
                              maxWidth: "100%",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {download.context.displayName}
                          </p>
                          <p
                            style={{
                              color: "grey",
                              fontSize: 12,
                              lineHeight: "10px",
                              marginBottom: 0,
                            }}
                          >
                            {downloadTypeMap[download.context.downloadType]}
                          </p>
                          <span
                            style={{
                              color: "grey",
                              fontSize: 12,
                              lineHeight: "10px",
                            }}
                          >
                            {(
                              moment(download.createdAt) >
                              moment().subtract(1, "day")
                            ) ?
                              moment(download.createdAt).fromNow()
                            : moment(download.createdAt).format("DD/MM/YYYY")}
                            {download.status === "failed" ?
                              <span
                                style={{
                                  color: "#e7381c",
                                  fontSize: 12,
                                  marginTop: -2,
                                }}
                              >
                                {" "}
                                - Download failed
                              </span>
                            : null}
                            {download.status === "expired" ?
                              <span style={{ fontSize: 12, marginTop: -2 }}>
                                {" "}
                                - Download expired
                              </span>
                            : null}
                          </span>
                        </div>
                      </div>
                      {download.status === "success" ?
                        <Icon name="download" />
                      : null}
                    </StyledDownloadContainer>
                  </Menu.Item>
                }
                content={
                  <div style={{ maxWidth: 300 }}>
                    {download.status === "failed" ?
                      "Something went wrong when trying to generate this file. Please try again, or contact us directly if this issue persists."
                    : "This download link has expired, please generate the download again if needed."
                    }
                  </div>
                }
              />
            ))}
          </>
        )}
      </DownloadsContainer>
    </div>
  );
};

export default Downloads;
